import CheckIcon from '@mui/icons-material/Check'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import {Alert, Box, Button, Chip, TextField, Typography} from '@mui/material'
import {useState} from 'react'

import {usePlantMonitoring, usePlantMonitoringSelectors} from '../context/PlantMonitoringContext'
import {
  feedbackCategories,
  FeedbackCategory,
  FeedbackRating,
  useGetFeedback,
  usePostFeedback
} from '../hooks/useFeedback'

export const Feedback = () => {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const {
    state: {feedbackMetaData}
  } = usePlantMonitoring()
  const {selectedIds} = usePlantMonitoringSelectors()

  const {data, isPending} = useGetFeedback({
    alertInstanceId: feedbackMetaData?.alertInstanceId,
    subjectCategory: feedbackMetaData?.subjectCategory
  })

  const {mutate, isSuccess} = usePostFeedback()

  const handlePostFeedback = ({
    rating,
    categories,
    comment
  }: {
    rating: FeedbackRating
    categories?: FeedbackCategory[]
    comment?: string
  }) => {
    if (!feedbackMetaData) return
    const {alertInstanceId, subjectCategory, alertStart, alertEnd, signalStart, signalEnd} =
      feedbackMetaData
    mutate({
      alertInstanceId,
      subjectCategory,
      rating,
      localNames: selectedIds,
      alertTimeRange: {
        start: alertStart,
        end: alertEnd
      },
      signalTimeRange: signalStart ? {start: signalStart, end: signalEnd} : undefined,
      categories,
      comment
    })
  }

  const currentFeedback = data?.[0]?.rating

  if (!feedbackMetaData || isPending) {
    return null
  }

  if (isSuccess) {
    return <Alert severity="success">Your feedback has been submitted successfully!</Alert>
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      gap={2}
      minHeight={72}
      border="1px solid #EEDEFA"
      sx={{backgroundColor: '#F9F4FD', borderRadius: '8px'}}
    >
      {showFeedbackForm ? (
        <FeedbackForm
          onCancel={() => setShowFeedbackForm(false)}
          onSubmit={(categories, comment) =>
            handlePostFeedback({rating: FeedbackRating.Negative, categories, comment})
          }
        />
      ) : (
        <BaseFeedback
          onNegativeClick={() => setShowFeedbackForm(true)}
          onPositiveClick={() => handlePostFeedback({rating: FeedbackRating.Positive})}
          currentFeedback={currentFeedback}
        />
      )}
    </Box>
  )
}

const BaseFeedback = ({
  onNegativeClick,
  onPositiveClick,
  currentFeedback
}: {
  onNegativeClick: () => void
  onPositiveClick: () => void
  currentFeedback: FeedbackRating | undefined
}) => {
  const readOnly = currentFeedback !== undefined
  const negative = currentFeedback === FeedbackRating.Negative
  const positive = currentFeedback === FeedbackRating.Positive

  return (
    <>
      <Box display="flex" gap={2} alignItems="center">
        <Box
          width={4}
          sx={{backgroundColor: '#8F26E0', borderRadius: '4px', alignSelf: 'stretch'}}
        ></Box>
        <Typography variant="h4">Is this prediction helpful?</Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1} pr={2}>
        {!positive && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ThumbDownAltOutlinedIcon />}
            onClick={onNegativeClick}
            disabled={readOnly}
            sx={{
              border: '1px solid #E8ECF0',
              backgroundColor: '#FFFFFF'
            }}
          >
            Not helpful
          </Button>
        )}
        {!negative && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ThumbUpOutlinedIcon />}
            onClick={onPositiveClick}
            disabled={readOnly}
            sx={{
              border: '1px solid #E8ECF0',
              backgroundColor: '#FFFFFF'
            }}
          >
            Helpful
          </Button>
        )}
      </Box>
    </>
  )
}

const FeedbackForm = ({
  onCancel,
  onSubmit
}: {
  onCancel: () => void
  onSubmit: (categories: FeedbackCategory[], comment?: string) => void
}) => {
  const [selectedCategories, setSelectedCategories] = useState<FeedbackCategory[]>([])
  const [comment, setComment] = useState('')

  const otherCategorySelected = selectedCategories.includes('Other')

  return (
    <Box display="flex" flexDirection="column" gap={2} p={2} flexGrow={1}>
      <Typography>What could be improved?</Typography>
      {otherCategorySelected && (
        <TextField
          label="Comment"
          variant="filled"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          InputProps={{sx: {backgroundColor: 'white'}}}
        />
      )}
      <Box display="flex" gap={1} flexWrap="wrap">
        {feedbackCategories.map((category) => (
          <Chip
            key={category}
            label={category}
            onClick={() => {
              if (selectedCategories.includes(category)) {
                setSelectedCategories((prev) => prev.filter((selected) => selected !== category))
              } else {
                setSelectedCategories((prev) => [...prev, category])
              }
            }}
            variant={selectedCategories.includes(category) ? 'filled' : 'outlined'}
            color="secondary"
          />
        ))}
      </Box>
      <Box display="flex" alignSelf="flex-end" gap={2}>
        <Button variant="text" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckIcon />}
          sx={{minHeight: 48}}
          onClick={() => onSubmit(selectedCategories, comment.trim() || undefined)}
          disabled={
            selectedCategories.length === 0 || (otherCategorySelected && comment.trim() === '')
          }
        >
          Send response
        </Button>
      </Box>
    </Box>
  )
}
